import type { Option } from "components/Forms/SelectCombo/SelectCombo";
import {
  type EnergyUnits,
  type Rounding,
  type VolumeUnits,
  energyUnitsEnum,
  roundingEnum,
  volumeUnitsEnum,
} from "kubb";
import { getUnitsForParameter } from "./model";

export type ParameterCategory = "energy" | "volume" | "other";

export type UnitsValue = EnergyUnits | VolumeUnits | string | null;

// Active energy parameters that support Wh/kWh/MWh conversion.
// Reactive energy parameters (Electrical Reactive Import/Export) use VAR units
// and are intentionally excluded — see backend ENERGY_PARAMETERS in
// time_series/domain/data/aggregations/query/scheme.py for the full list.
const ENERGY_PARAMETERS = [
  "Energy (Heating)",
  "Energy (Cooling)",
  "Energy (Electrical Active Import)",
  "Energy (Electrical Active Export)",
];

const VOLUME_PARAMETERS = ["Volume"];

/**
 * Determines the category of a parameter based on its name.
 * Energy parameters can be converted between Wh, kWh, and MWh.
 * Volume parameters can be converted between m3 and litres.
 * Other parameters have a fixed unit.
 */
export function getParameterCategory(parameter: string): ParameterCategory {
  if (ENERGY_PARAMETERS.includes(parameter)) {
    return "energy";
  }
  if (VOLUME_PARAMETERS.includes(parameter)) {
    return "volume";
  }
  return "other";
}

/**
 * Returns the available unit options for a given parameter.
 * Energy parameters get energy unit options (kWh, Wh, MWh).
 * Volume parameters get volume unit options (m3, litres).
 * Other parameters get a single option with their base unit.
 */
export function getUnitsOptionsForParameter(parameter: string): Option[] {
  const category = getParameterCategory(parameter);

  switch (category) {
    case "energy":
      return [
        { label: "Wh", value: energyUnitsEnum.Wh },
        { label: "kWh", value: energyUnitsEnum.kWh },
        { label: "MWh", value: energyUnitsEnum.MWh },
      ];
    case "volume":
      return [
        { label: "m\u00B3", value: volumeUnitsEnum.m3 },
        { label: "Litres", value: volumeUnitsEnum.litres },
      ];
    default: {
      const baseUnit = getUnitsForParameter(parameter);
      return [{ label: baseUnit, value: baseUnit }];
    }
  }
}

/**
 * Returns the default unit for a given parameter.
 * Energy parameters default to kWh.
 * Volume parameters default to m3.
 * Other parameters default to their base unit.
 */
export function getDefaultUnitsForParameter(parameter: string): UnitsValue {
  const category = getParameterCategory(parameter);

  switch (category) {
    case "energy":
      return energyUnitsEnum.kWh;
    case "volume":
      return volumeUnitsEnum.m3;
    default:
      return getUnitsForParameter(parameter);
  }
}

/**
 * Determines the appropriate rounding setting based on the selected unit.
 * ONE_DP for kWh, MWh, and litres.
 * NONE for Wh, m3, and all other units.
 */
export function getRoundingForUnits(units: UnitsValue): Rounding {
  switch (units) {
    case energyUnitsEnum.kWh:
    case energyUnitsEnum.MWh:
    case volumeUnitsEnum.litres:
      return roundingEnum.ONE_DP;
    default:
      return roundingEnum.NONE;
  }
}

/**
 * Checks whether a parameter supports unit conversion.
 * Only energy and volume parameters support conversion.
 */
export function isConvertibleParameter(parameter: string): boolean {
  const category = getParameterCategory(parameter);
  return category === "energy" || category === "volume";
}

// Keep in sync with management-api/src/schema/common.py
export const DATA_QUALITY_AGGREGATIONS = [
  "expected_rows",
  "received_rows_complete",
  "received_rows_incomplete",
  "received_rows_with_errors",
  "received_rows_basic_complete",
  "received_rows_with_value_errors",
  "expected_data_points",
  "good_data_points",
  "value_error_data_points",
];

export const NON_SCALABLE_AGGREGATIONS = [
  "count",
  ...DATA_QUALITY_AGGREGATIONS,
];

/**
 * Checks whether an aggregation type supports unit conversion.
 * Count and data quality aggregations are not physical measurements
 * and should never have unit scaling applied.
 */
export function aggregationSupportsUnits(aggregation: string): boolean {
  return !NON_SCALABLE_AGGREGATIONS.includes(aggregation);
}
